/**
 * Consent state holder.
 *
 * Privacy/CMP resolution lives in `src/privacy/manager.ts`; this class owns
 * only status, manual override, awaiter completion, and CONSENT_CHANGED events.
 */

import { AwaitFor, completeAwait, isAwaitCompleted } from '../events/awaiter';
import { emit, SdkEvent } from '../events/bus';

export type ConsentStatus = 'unknown' | 'granted' | 'denied';
export type LoadCmpMode = 'auto' | 'never';

export class ConsentEngine {
  private status: ConsentStatus = 'unknown';
  private overrideStatus: 'granted' | 'denied' | null = null;

  getStatus(): ConsentStatus {
    if (this.overrideStatus) return this.overrideStatus;
    return this.status;
  }

  /** Manual override from `KueezSDK.consent.set()`. Always wins. */
  setOverride(value: 'granted' | 'denied'): void {
    const prev = this.getStatus();
    this.overrideStatus = value;
    this.commit(value, prev);
  }

  /** Used by `privacy.optOut/optIn` — transitions without setting the override flag. */
  setResolved(value: 'granted' | 'denied'): void {
    const prev = this.getStatus();
    this.status = value;
    this.commit(value, prev);
  }

  /** `prev` is the status BEFORE the caller mutated state. */
  private commit(value: 'granted' | 'denied', prev: ConsentStatus): void {
    if (!isAwaitCompleted(AwaitFor.CONSENT)) {
      completeAwait(AwaitFor.CONSENT, value);
    }
    const next = this.getStatus();
    if (next !== prev && (next === 'granted' || next === 'denied')) {
      emit(SdkEvent.CONSENT_CHANGED, { status: next, prev });
    }
  }
}
